/**
 * Thin fetch wrapper for the same origin API routes.
 *
 * Sessions are carried by a secure, HttpOnly, SameSite cookie set by the server,
 * so credentials are always included and no token is ever held in JavaScript.
 */

export interface ApiError {
  message: string
  status: number
}

export class ApiRequestError extends Error {
  status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiRequestError'
    this.status = status
  }
}

const TIMEOUT_MS = 15000

async function request<TResponse>(
  path: string,
  init: RequestInit & { timeoutMs?: number } = {},
): Promise<TResponse> {
  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), init.timeoutMs ?? TIMEOUT_MS)

  try {
    const response = await fetch(path, {
      ...init,
      credentials: 'include',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(init.headers ?? {}),
      },
    })

    const isJson = response.headers.get('content-type')?.includes('application/json')
    const payload = isJson ? await response.json().catch(() => null) : null

    if (!response.ok) {
      const message =
        (payload && typeof payload === 'object' && 'error' in payload
          ? String((payload as { error: unknown }).error)
          : null) ??
        (response.status === 429
          ? 'Too many attempts. Please wait a moment and try again.'
          : 'Something went wrong at our end. Please try again.')
      throw new ApiRequestError(message, response.status)
    }

    return payload as TResponse
  } catch (error) {
    if (error instanceof ApiRequestError) throw error
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiRequestError('That request timed out. Please try again.', 408)
    }
    throw new ApiRequestError('We could not reach the server. Please check your connection.', 0)
  } finally {
    window.clearTimeout(timeout)
  }
}

export function apiGet<TResponse>(path: string): Promise<TResponse> {
  return request<TResponse>(path, { method: 'GET' })
}

export function apiPost<TResponse>(
  path: string,
  body: unknown,
  options: { timeoutMs?: number } = {},
): Promise<TResponse> {
  return request<TResponse>(path, {
    method: 'POST',
    body: JSON.stringify(body),
    timeoutMs: options.timeoutMs,
  })
}
